// Meta Pixel tracking utility functions
declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

import { hasCookieConsent } from './consentUtils';

const META_PIXEL_QUEUE_KEY = 'meta_pixel_queue';
const MAX_QUEUE_SIZE = 50; // Prevent unbounded queue growth

type QueuedEvent = { eventName: string; parameters?: Record<string, unknown> };

// Helper to get queue from sessionStorage with validation
const getQueue = (): QueuedEvent[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const stored = sessionStorage.getItem(META_PIXEL_QUEUE_KEY);
    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    // Validate parsed data is an array
    if (typeof parsed !== 'object' || !Array.isArray(parsed)) {
      return [];
    }

    // Validate and sanitize each item
    const validated: QueuedEvent[] = [];
    for (const item of parsed) {
      // Only keep valid items with string eventName
      if (
        item &&
        typeof item === 'object' &&
        typeof item.eventName === 'string' &&
        (item.parameters === undefined ||
          (typeof item.parameters === 'object' && item.parameters !== null))
      ) {
        validated.push({
          eventName: item.eventName,
          parameters: item.parameters || undefined,
        });
      }
    }

    return validated;
  } catch {
    // sessionStorage access failed or JSON parse error (corrupted data)
    return [];
  }
};

// Helper to save queue to sessionStorage
const saveQueue = (queue: QueuedEvent[]): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.setItem(META_PIXEL_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // sessionStorage write failed (quota exceeded, private browsing, etc.)
    // Events will be lost but this is acceptable for tracking
  }
};

// Helper to clear queue from sessionStorage
const clearQueue = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(META_PIXEL_QUEUE_KEY);
  } catch {
    // sessionStorage clear failed - not critical
  }
};

export const isMetaPixelReady = (): boolean => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function' && hasCookieConsent();
};

export const trackMetaPixelEvent = (eventName: string, parameters?: Record<string, unknown>) => {
  if (typeof window === 'undefined') {
    return;
  }

  // If Meta Pixel is ready and consent is given, track immediately
  if (isMetaPixelReady()) {
    try {
      window.fbq?.('track', eventName, parameters);
    } catch (error) {
      console.error('[Meta Pixel] Failed to track event:', eventName, error);
    }
    return;
  }

  // Otherwise, queue the event for later (persists across page reloads in same session)
  const queue = getQueue();

  // Enforce max queue size to prevent unbounded growth
  if (queue.length >= MAX_QUEUE_SIZE) {
    // Remove oldest event when queue is full
    queue.shift();
  }

  queue.push({ eventName, parameters });
  saveQueue(queue);
};

// Process any queued events once Meta Pixel is ready
export const processMetaPixelQueue = () => {
  if (typeof window === 'undefined' || !isMetaPixelReady()) {
    return;
  }

  const queue = getQueue();

  if (queue.length === 0) {
    return;
  }

  // Process all queued events
  for (const { eventName, parameters } of queue) {
    try {
      window.fbq?.('track', eventName, parameters);
    } catch (error) {
      console.error('[Meta Pixel] Failed to track queued event:', eventName, error);
    }
  }

  // Clear the queue after processing
  clearQueue();
};

// Track when a user starts a trial
export const trackStartTrial = () => {
  trackMetaPixelEvent('StartTrial');
};

// Track when a user completes a purchase (after trial ends)
export const trackPurchase = (content_ids?: string[]) => {
  trackMetaPixelEvent('Purchase', {
    content_ids,
  });
};

// Track when a user subscribes (this can be used for both trial start and actual subscription)
export const trackSubscribe = (content_ids?: string[]) => {
  trackMetaPixelEvent('Subscribe', {
    content_ids,
  });
};

// Track when a user initiates checkout
export const trackInitiateCheckout = (content_ids?: string[]) => {
  trackMetaPixelEvent('InitiateCheckout', {
    content_ids,
  });
};
